import { UseFormReturn, useWatch } from "react-hook-form";
import { WizardSchemaType } from "../index";
import React, { useEffect, useMemo, useState } from "react";
import { AccordionDetails, Button, Stack } from "@mui/material";
import TextMaxLine from "@onair/ui/text-max-line";
import { Iconify } from "@onair/minimal-ui/iconify";
import EventSessionNumberBadge from "./event-session-number-badge";
import {
	EventSessionAccordion,
	EventSessionAccordionSummary,
} from "./event-session-accordion";
import EventRoomSelect from "../event-room-select";
import { ExtendRoomProvider } from "@onair/constants";
import { SessionPriceType } from "@onair/repositories";
import has from "lodash/has";

interface Props {
	index: number;
	methods: UseFormReturn<WizardSchemaType>;
}

const EventSessionRoomItem = ({ index, methods }: Props) => {
	const { control, getValues, setValue, unregister, getFieldState } = methods;

	const { error } = getFieldState(`stepPricing.sessions.${index}`);

	const isError = useMemo(() => {
		const fields: (keyof WizardSchemaType["stepPricing"]["sessions"][0])[] = [
			"roomProvider",
			"roomUrl",
			"roomInfo",
		];

		return fields.some((field) => has(error, field));
	}, [error]);

	const eventSessionName = useWatch({
		name: `stepTimeline.sessions.${index}.name`,
		control,
	});
	const roomProvider = useWatch({
		name: `stepPricing.sessions.${index}.roomProvider`,
		control,
	});
	const priceType = useWatch({
		name: "stepPricing.priceType",
		control,
	});
	const isFreeOnAll = useWatch({
		name: "stepPricing.isFree",
		control,
	});
	const isFreeOnEach = useWatch({
		name: `stepPricing.sessions.${index}.isFree`,
		control,
	});

	const isFree = useMemo(() => {
		if (priceType === SessionPriceType.ALL) {
			return isFreeOnAll;
		}
		return isFreeOnEach;
	}, [priceType, isFreeOnAll, isFreeOnEach]);

	useEffect(() => {
		if (isFree) {
			if (
				getValues(`stepPricing.sessions.${index}.roomProvider`) ===
				ExtendRoomProvider.SYSTEM
			) {
				unregister(`stepPricing.sessions.${index}.roomProvider`);
				// @ts-ignore
				setValue(`stepPricing.sessions.${index}.roomProvider`, null);
			}
		}
	}, [isFree]);

	const header = useMemo(
		() => (
			<Stack
				direction={{
					xs: "column",
					md: "row",
				}}
				alignItems={{
					xs: "flex-start",
					md: "center",
				}}
				spacing={1}
			>
				<EventSessionNumberBadge number={index + 1} />
				<TextMaxLine
					line={1}
					variant="subtitle2"
					lineHeight="20px"
					fontWeight={600}
				>
					{eventSessionName || "Tên sự kiện"}
				</TextMaxLine>
			</Stack>
		),
		[eventSessionName, index],
	);

	return (
		<EventSessionAccordion>
			<EventSessionAccordionSummary
				isError={isError}
				expandIcon={
					<Button
						variant="soft"
						sx={{
							backgroundColor: "white",
							width: 34,
							minWidth: "unset",
						}}
					>
						<Iconify
							color="action.active"
							width={18}
							icon="eva:arrow-ios-downward-fill"
						/>
					</Button>
				}
			>
				<Stack
					width="100%"
					direction="row"
					justifyContent="space-between"
					alignItems={{
						xs: "flex-start",
						md: "center",
					}}
				>
					<Stack
						direction={{
							xs: "column",
							md: "row",
						}}
						alignItems={{
							xs: "flex-start",
							md: "end",
						}}
						justifyContent={{
							xs: "flex-start",
							md: "space-between",
						}}
						spacing={1}
					>
						{header}
					</Stack>
				</Stack>
			</EventSessionAccordionSummary>
			<AccordionDetails
				sx={{
					pt: {
						xs: 0,
						md: 2,
					},
				}}
			>
				<EventRoomSelect
					prefixName={`stepPricing.sessions.${index}`}
					isFree={isFree}
					roomProvider={roomProvider}
				/>
			</AccordionDetails>
		</EventSessionAccordion>
	);
};

export default EventSessionRoomItem;
